import { useCallback, useMemo, useState, useEffect } from "react";
import { IGroup } from "@fluentui/react";
import {
  ColumnFiltersState,
  OnColumnFilterChange,
} from "@cayo/shared/components/GridContainer/types/columnFilters";
import { filterGroupedItems, normalizeColumnFilters, matchesColumnFilters } from "../utils";

interface UseColumnFiltersProps<TItem> {
  items: ReadonlyArray<TItem>;
  groups?: ReadonlyArray<IGroup>;
}

interface UseColumnFiltersResult<TItem> {
  columnFilters: ColumnFiltersState;
  onColumnFilterChange: OnColumnFilterChange;
  filteredItems: Array<TItem>;
  filteredGroups?: Array<IGroup>;
}

const defaultColumnFilterDelay = 300;

export const useColumnFilters = <TItem extends Record<string, unknown>>({
  items,
  groups,
}: UseColumnFiltersProps<TItem>): UseColumnFiltersResult<TItem> => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>({});
  const [appliedColumnFilters, setAppliedColumnFilters] = useState<ColumnFiltersState>({});

  const onColumnFilterChange = useCallback<OnColumnFilterChange>((fieldName, value) => {
    setColumnFilters((currentColumnFilters) => ({
      ...currentColumnFilters,
      [fieldName]: value,
    }));
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAppliedColumnFilters(columnFilters);
    }, defaultColumnFilterDelay);

    return () => clearTimeout(timeoutId);
  }, [columnFilters]);

  const activeColumnFilters = useMemo(
    () => normalizeColumnFilters(appliedColumnFilters),
    [appliedColumnFilters]
  );

  const filteredResult = useMemo(() => {
    if (!Object.keys(activeColumnFilters).length) {
      return {
        filteredItems: [...items],
        filteredGroups: groups ? [...groups] : undefined,
      };
    }

    if (groups?.length) {
      return filterGroupedItems(items, groups, activeColumnFilters);
    }

    return {
      filteredItems: items.filter((item) => matchesColumnFilters(item, activeColumnFilters)),
      filteredGroups: undefined,
    };
  }, [activeColumnFilters, groups, items]);

  return {
    columnFilters,
    onColumnFilterChange,
    filteredItems: filteredResult.filteredItems,
    filteredGroups: filteredResult.filteredGroups,
  };
};
